import React, { useCallback, useEffect, useState } from "react";
import {
  ArrowRight,
  Calendar,
  PackageSearch,
  RefreshCw,
  RotateCcw,
  Repeat,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LoadingComponent from "../components/LoadingComponent";
import Pagination from "../components/Pagination";
import Price from "../components/Currency/Price";
import { ReturnsApi } from "../services/returns-api";
import {
  ReturnRequest,
  ReturnRequestStatus,
  ReturnsPagination,
} from "../types/ReturnRequest";

const STATUS_COLORS: Record<ReturnRequestStatus, string> = {
  requested: "bg-yellow-100 text-yellow-800 border-yellow-200",
  approved: "bg-blue-100 text-blue-800 border-blue-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  driver_assigned: "bg-indigo-100 text-indigo-800 border-indigo-200",
  pickup_scheduled: "bg-indigo-100 text-indigo-800 border-indigo-200",
  picked_up: "bg-purple-100 text-purple-800 border-purple-200",
  pickup_rejected: "bg-red-100 text-red-800 border-red-200",
  refund_initiated: "bg-blue-100 text-blue-800 border-blue-200",
  refund_completed: "bg-green-100 text-green-800 border-green-200",
  refund_failed: "bg-red-100 text-red-800 border-red-200",
  payment_required: "bg-orange-100 text-orange-800 border-orange-200",
  completed: "bg-green-100 text-green-800 border-green-200",
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const humanizeStatus = (status: string) =>
  status.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

const MyReturns: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ReturnRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pagination, setPagination] = useState<ReturnsPagination>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ReturnsApi.listMine(currentPage, itemsPerPage);
      if (res?.success) {
        setRequests(Array.isArray(res.data) ? res.data : []);
        setPagination(
          res.pagination || {
            currentPage,
            totalPages: 1,
            totalItems: res.data?.length || 0,
            itemsPerPage,
          },
        );
      }
    } catch (error) {
      console.error("Failed to load return requests", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  if (loading) {
    return <LoadingComponent title={t("Returns and Exchanges")} />;
  }

  return (
    <div className="min-h-screen bg-[#f2e6db] px-4 py-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="rounded-2xl bg-white p-6 shadow-xl sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-black p-4">
                <PackageSearch className="h-8 w-8 text-white sm:h-6 sm:w-6" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">
                  {t("Returns and Exchanges")}
                </h1>
                <p className="text-gray-600">
                  {t("Track your return and exchange requests")}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={fetchRequests}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-100 px-4 py-3 font-semibold text-gray-700 transition-colors hover:bg-gray-200"
            >
              <RefreshCw className="h-4 w-4" />
              {t("Refresh")}
            </button>
          </div>
        </div>

        {requests.length === 0 ? (
          <div className="rounded-2xl bg-white p-10 text-center shadow-xl">
            <RotateCcw className="mx-auto h-14 w-14 text-gray-300" />
            <h2 className="mt-4 text-xl font-bold text-gray-900">
              {t("No return or exchange requests yet")}
            </h2>
            <p className="mt-2 text-gray-600">
              {t("Eligible delivered orders can be returned or exchanged from order details.")}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => {
              const order = request.order as any;
              const isExchange = request.type === "exchange";
              return (
                <button
                  key={request.id}
                  type="button"
                  onClick={() => navigate(`/account/returns/${request.id}`)}
                  className="w-full rounded-2xl bg-white p-5 text-start shadow-lg transition-all hover:shadow-xl"
                >
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-4">
                      <div
                        className={`rounded-xl p-3 ${
                          isExchange ? "bg-blue-100" : "bg-gray-100"
                        }`}
                      >
                        {isExchange ? (
                          <Repeat className="h-6 w-6 text-blue-700" />
                        ) : (
                          <RotateCcw className="h-6 w-6 text-gray-700" />
                        )}
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-lg font-bold text-gray-900">
                            {order?.orderNumber
                              ? `${t("Order")} #${order.orderNumber}`
                              : t("Return request")}
                          </h3>
                          <span
                            className={`rounded-full px-3 py-1 text-xs font-semibold ${
                              isExchange
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {isExchange ? t("Exchange") : t("Return")}
                          </span>
                          <span
                            className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                              STATUS_COLORS[request.status] ||
                              "bg-gray-100 text-gray-700 border-gray-200"
                            }`}
                          >
                            {t(humanizeStatus(request.status))}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-600">
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(request.createdAt)}
                          </span>
                          {order?.total !== undefined && (
                            <span>
                              {t("Order Total")}:{" "}
                              <span className="font-semibold text-gray-900">
                                <Price amount={Number(order.total)} />
                              </span>
                            </span>
                          )}
                          <span>
                            {t("Items")}: {request.items?.length || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                    <ArrowRight className="hidden h-5 w-5 text-gray-400 sm:block" />
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {pagination.totalItems > 0 && (
          <div className="rounded-2xl bg-white px-4">
            <Pagination
              currentPage={pagination.currentPage || currentPage}
              totalPages={pagination.totalPages || 1}
              totalItems={pagination.totalItems || 0}
              itemsPerPage={pagination.itemsPerPage || itemsPerPage}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                setCurrentPage(1);
              }}
              pageSizeOptions={[10, 20, 50]}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MyReturns;
